/* ═══════════════════════════════════════════════════════════════
   KALKI Fashion — Size Guide modal
   Vanilla custom element. No dependencies. Loaded only on the product
   page via snippets/size-guide.liquid.

   • Opens from any element with [data-size-guide-open] (the existing
     "Size Guide" link near the size selector). Progressive enhancement:
     without JS the link still navigates to its href.
   • Accessible modal: focus trap, Esc, backdrop / ✕ close, focus return.
   • Inches ⇄ CM toggle. Measurement cells store inches in data-in;
     cm is computed (×2.54, rounded) so the merchant only enters one set
     of numbers. Works for ranges too (each number in the cell converts).
   ════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.modal = this.querySelector('[data-sg-modal]');
      this.unitButtons = Array.from(this.querySelectorAll('[data-sg-unit]'));
      this.numCells = Array.from(this.querySelectorAll('[data-in]'));
      this.unitLabels = Array.from(this.querySelectorAll('[data-sg-unit-label]'));

      this._onKeydown = (e) => this.onKeydown(e);

      // Triggers live outside this element (the size-selector "Size Guide"
      // link). Intercept their click and open the modal instead.
      this.triggers = Array.from(document.querySelectorAll('[data-size-guide-open]'));
      this.triggers.forEach((t) =>
        t.addEventListener('click', (e) => { e.preventDefault(); this.openModal(); })
      );

      this.querySelectorAll('[data-sg-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeModal())
      );

      this.unitButtons.forEach((b) =>
        b.addEventListener('click', () => this.setUnit(b.dataset.sgUnit))
      );

      // Default unit (configurable via the snippet's data attribute).
      this.setUnit(this.dataset.defaultUnit === 'cm' ? 'cm' : 'in');

      // Theme editor: open when a size-guide row block is selected.
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          if (this.contains(e.target)) this.openModal();
        });
        document.addEventListener('shopify:block:deselect', (e) => {
          if (this.contains(e.target)) this.closeModal();
        });
      }
    }

    // ════════════════════════ UNITS ════════════════════════════
    setUnit(unit) {
      const toCm = unit === 'cm';
      this.numCells.forEach((cell) => {
        const inches = cell.dataset.in || '';
        cell.textContent = toCm ? convertToCm(inches) : inches;
      });
      this.unitLabels.forEach((l) => { l.textContent = toCm ? '(cm)' : '(in)'; });
      this.unitButtons.forEach((b) =>
        b.setAttribute('aria-pressed', b.dataset.sgUnit === unit ? 'true' : 'false')
      );
    }

    // ════════════════════════ MODAL ════════════════════════════
    openModal() {
      if (!this.modal) return;
      this.lastFocused = document.activeElement;
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);
      const target = this.modal.querySelector('.sg-modal-close');
      if (target) requestAnimationFrame(() => target.focus());
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    onKeydown(e) {
      if (!this.modal || this.modal.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); this.closeModal(); return; }
      if (e.key !== 'Tab') return;

      const panel = this.modal.querySelector('.sg-modal-panel');
      if (!panel) return;
      const f = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  // Convert every number found in a string from inches to cm (rounded).
  // Non-numeric text (dashes, slashes, units) is preserved, so "32-34"
  // becomes "81-86" and a blank stays blank.
  function convertToCm(str) {
    return String(str).replace(/\d+(\.\d+)?/g, (m) => String(Math.round(parseFloat(m) * 2.54)));
  }

  if (!customElements.get('size-guide')) {
    customElements.define('size-guide', SizeGuide);
  }
})();
